import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ListMeta } from '@axsolutionsdev/shared';
import { isApiSuccess } from '@axsolutionsdev/shared';
import type { Router } from 'vue-router';

import type { AxiosInterceptor } from '../types/app';
import {
  buildLoginPath,
  isLoginRoutePath,
  resolveLoginRedirectTenantCode,
} from '../router/login-route.js';
import { useAuthStore } from '../stores/auth';
import { ApiClientError } from './api-error';
import { refreshRequest } from './auth';

/**
 * Core API 클라이언트.
 *
 * baseURL은 vite proxy(`/api → http://localhost:50002`)를 통한다.
 * (포트 체계: docs/09-port-allocation.md §1)
 * 환경변수 VITE_API_BASE_URL이 있으면 그 값을 우선 적용 (Tier 4 on-prem 등).
 *
 * **싱글턴 권장**: `getApiClient()` 가 진입점. 인스턴스가 1개여야 인증 인터셉터/
 * 토큰 첨부 동작이 일관된다. 외부에서 별도 인스턴스가 필요한 특수 케이스에만
 * `createApiClient()` 를 직접 호출한다 (이 경우 인터셉터는 별도 부착 책임).
 */
export function createApiClient(): AxiosInstance {
  const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

  const client = axios.create({
    baseURL,
    withCredentials: true, // Refresh Token 이 HttpOnly 쿠키이므로 (docs/04 §2.2)
    timeout: 30_000,
  });

  return client;
}

// =============================================================================
// 싱글턴 — 토큰 자동 첨부 + 401 → refresh → 재시도 인터셉터를 부착한 instance.
//
// createAxApp 가 pinia 를 set up 한 직후 `setApiRouter(router)` + `getApiClient()`
// 순으로 호출한다. 인터셉터 안에서 `useAuthStore()` 를 호출하므로 pinia 가
// 반드시 먼저 install 되어 있어야 한다.
//
// 401 처리 정책 (E.2 + E.3 결합):
//   - /auth/login 자체의 401 → 로그인 폼이 받아 메시지 표시. skip.
//   - X-Skip-Auth-Refresh 헤더가 있는 요청(refresh/logout 자체) → skip.
//   - 그 외 401 → /api/auth/refresh 1회 시도.
//       성공: 새 access token 으로 원 요청 재시도.
//       실패: authStore.logout() + /login?redirect=... 이동.
//   - 동일 요청이 두 번 401 되는 무한루프 방지를 위해 _retry flag 사용.
//   - 동시다발 401 이 들어와도 refresh 호출은 1회로 합쳐지도록 in-flight Promise 공유.
// =============================================================================

/** 인터셉터 내부에서 _retry flag 를 부착하기 위한 확장 타입. */
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let _client: AxiosInstance | null = null;
let _router: Router | null = null;

/** createAxApp 에서 router 생성 직후 호출. 401 발생 시 redirect 에 사용. */
export function setApiRouter(router: Router): void {
  _router = router;
}

/** 싱글턴 axios 인스턴스. 첫 호출 시 default 인터셉터를 부착한다. */
export function getApiClient(): AxiosInstance {
  if (!_client) {
    _client = createApiClient();
    attachDefaultInterceptors(_client);
  }
  return _client;
}

/** 동시 401 발생 시 refresh 호출 중복 방지용 in-flight 핸들. */
let _refreshInFlight: Promise<string | null> | null = null;

function attachDefaultInterceptors(client: AxiosInstance): void {
  // ---- request: Authorization Bearer 자동 첨부 -----------------------------
  client.interceptors.request.use((config) => {
    try {
      const auth = useAuthStore();
      if (auth.accessToken) {
        config.headers.set('Authorization', `Bearer ${auth.accessToken}`);
      }
    } catch {
      // pinia 가 아직 install 안 된 환경 (테스트 boot 등) 은 조용히 skip.
    }
    return config;
  });

  // ---- response: success envelope unwrap (M.2, R4.B.2) ---------------------
  // BE 의 ResponseEnvelopeInterceptor 가 모든 success 응답을 `{ data, meta? }`
  // envelope 으로 감싼다 (auth/* 의 @RawResponse() 우회 제외). FE 의 호출부
  // 변경 비용을 최소화하기 위해, 본 인터셉터가 envelope 을 자동으로 unwrap
  // 하여 `res.data` 를 envelope 의 `.data` 로 대체한다.
  //
  //   BE 응답 body                   →   res.data           res._meta
  //   ---------------------------------------------------------------------
  //   { data: { id: 'u1' } }         →   { id: 'u1' }       undefined
  //   { data: [...], meta: {...} }   →   [...]              meta
  //   { accessToken, user }          →   동일               undefined  (raw)
  //   { items: [...] }               →   { items: [...] }   undefined  (legacy
  //                                                                    경로 보존)
  //
  // 호출부에서 list 메타가 필요하면 `getResponseMeta(res)` helper 사용.
  client.interceptors.response.use(unwrapEnvelopeResponse);

  // ---- response: 401 → refresh → 재시도 -----------------------------------
  client.interceptors.response.use(
    (res) => res,
    async (err: AxiosError) => {
      const status = err.response?.status;
      const config = err.config as RetriableConfig | undefined;
      const url = config?.url ?? '';
      const isLoginCall = url.includes('/auth/login');
      const skipRefresh =
        url.includes('/auth/refresh') ||
        url.includes('/auth/logout') ||
        getHeader(config, 'X-Skip-Auth-Refresh') === '1';

      if (status !== 401 || isLoginCall || skipRefresh || !config || config._retry) {
        return Promise.reject(rejectWithApiError(err));
      }

      config._retry = true;

      const newToken = await refreshAccessTokenOnce(client);
      if (newToken) {
        // 헤더 갱신 후 원 요청 재시도. 새 인스턴스 호출이 아니라 동일 client 로
        // 재발행하므로 인터셉터 chain 은 재진입하지 않는다 (axios 내부 동작).
        config.headers.set('Authorization', `Bearer ${newToken}`);
        return client.request(config);
      }

      // refresh 실패 → 세션 정리 + 로그인 화면으로.
      await handleAuthFailureRedirect();
      return Promise.reject(rejectWithApiError(err));
    },
  );
}

/**
 * BE 의 표준 에러 envelope 이 담겨 있으면 ApiClientError 로 wrap, 그 외(network
 * timeout, CORS, non-envelope 응답) 는 원본 AxiosError 를 그대로 반환한다.
 *
 * 호출부가 `if (err instanceof ApiClientError)` 분기로 일관된 에러 처리를 작성할
 * 수 있다. 401 → refresh 재시도 path 와는 독립이라 (refresh 성공 시에는 본 함수가
 * 호출되지 않음) wrap 대상은 "사용자에게 결국 보일 에러" 만이다.
 *
 * 반환 타입을 Error 서브타입으로 좁혀 lint(`prefer-promise-reject-errors`) 와
 * 호출부의 instanceof 분기 양쪽 모두 안전하게 한다 (AxiosError 도 Error).
 */
function rejectWithApiError(err: AxiosError): Error {
  const wrapped = ApiClientError.fromAxios(err);
  if (wrapped) return wrapped;
  if (err.response?.status === 413) {
    return new Error('파일 크기가 허용 한도를 초과했습니다. 더 작은 파일을 등록해 주세요.');
  }
  return err;
}

/**
 * refresh 를 동시 다발적으로 호출하지 않도록 in-flight Promise 를 공유한다.
 * 성공 시 새 access token 문자열, 실패/오류 시 null 을 반환한다.
 */
async function refreshAccessTokenOnce(client: AxiosInstance): Promise<string | null> {
  if (!_refreshInFlight) {
    _refreshInFlight = (async () => {
      try {
        const { accessToken } = await refreshRequest(client);
        try {
          const auth = useAuthStore();
          auth.setAccessToken(accessToken);
        } catch {
          // pinia 가 없으면 그냥 토큰 반환만.
        }
        return accessToken;
      } catch {
        return null;
      } finally {
        // 다음 401 사이클에서는 새로 시도 가능하도록 release.
        _refreshInFlight = null;
      }
    })();
  }
  return _refreshInFlight;
}

async function handleAuthFailureRedirect(): Promise<void> {
  const mode = import.meta.env.VITE_DEPLOYMENT_MODE === 'on-prem' ? 'on-prem' : ('saas' as const);
  let userTenantCode: string | null | undefined;
  try {
    const auth = useAuthStore();
    userTenantCode = auth.user?.tenantCode;
    if (auth.accessToken || auth.user) {
      await auth.logout({ remote: false });
    }
  } catch {
    // best-effort
  }
  const router = _router;
  if (!router) return;
  const current = router.currentRoute.value.fullPath;
  const segment = resolveLoginRedirectTenantCode({
    mode,
    userTenantCode: userTenantCode ?? null,
  });
  const redirect = current && !isLoginRoutePath(current) && current !== '/' ? current : undefined;
  await router.push(buildLoginPath(segment, { redirect }));
}

function getHeader(config: AxiosRequestConfig | undefined, name: string): string | undefined {
  const headers = config?.headers;
  if (!headers) return undefined;
  // axios v1 의 헤더는 AxiosHeaders 인스턴스 또는 일반 객체.
  const get = (headers as { get?: (k: string) => unknown }).get;
  if (typeof get === 'function') {
    const v = get.call(headers, name);
    return typeof v === 'string' ? v : undefined;
  }
  const obj = headers as Record<string, unknown>;
  const v = obj[name];
  return typeof v === 'string' ? v : undefined;
}

// =============================================================================
// 응답 envelope unwrap helpers (R4.B.2 / M.2)
// =============================================================================

/** ListMeta 가 첨부된 axios response 의 확장 타입. */
type ResponseWithMeta<T = unknown> = AxiosResponse<T> & { _meta?: ListMeta };

/** 호출부에서 list 메타에 접근하기 위한 helper. */
export function getResponseMeta<T>(res: AxiosResponse<T>): ListMeta | undefined {
  return (res as ResponseWithMeta<T>)._meta;
}

/**
 * BE success envelope (`{ data, meta? }`) 을 자동으로 unwrap.
 *
 * 변환 규칙:
 *   - body 가 `{ data, meta? }` 형상이면 res.data 를 body.data 로 대체하고
 *     meta 를 (res as any)._meta 에 attach.
 *   - 그 외 (raw 응답, 빈 body, 비-객체 body) 는 통과.
 *
 * `'data' in body && !('error' in body)` 검사는 `isApiSuccess` 의 type guard
 * 활용. error envelope (`{ error: { ... } }`) 은 본 인터셉터가 통과시키고,
 * 후속 라운드 (R4.B.3) 에서 error 인터셉터가 axios reject path 로 변환한다.
 */
function unwrapEnvelopeResponse(res: AxiosResponse): AxiosResponse {
  const body: unknown = res.data;
  if (!isApiSuccess(body)) return res;
  const augmented = res as ResponseWithMeta<unknown>;
  augmented.data = body.data;
  if (body.meta !== undefined) {
    augmented._meta = body.meta as ListMeta;
  }
  return augmented;
}

/**
 * createAxApp 옵션의 apiInterceptors 를 axios 인스턴스에 등록한다.
 * (default 인터셉터 이후에 부착되므로 사용자 정의가 마지막에 동작한다.)
 */
export function attachInterceptors(client: AxiosInstance, interceptors: AxiosInterceptor[]): void {
  for (const interceptor of interceptors) {
    if (interceptor.request) {
      client.interceptors.request.use(
        interceptor.request.onFulfilled,
        interceptor.request.onRejected,
      );
    }
    if (interceptor.response) {
      client.interceptors.response.use(
        interceptor.response.onFulfilled,
        interceptor.response.onRejected,
      );
    }
  }
}
